// Cookie-consent state for the app (#486). Uses a cross-subdomain cookie
// (Domain=.tristanoscatering.ca) so the choice is shared with the marketing site —
// a visitor who declines on either site has that decision respected everywhere.
//
// Opt-out model (matches marketing): analytics run by default and stop only if
// the visitor explicitly declines. localStorage is not used here (unlike
// marketing's hybrid approach) because the primary consumer is the sister site,
// and localStorage is origin-bound.

export type ConsentChoice = "accepted" | "declined";

const COOKIE_NAME = "tc_consent";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year
const SHARED_PARENT_DOMAIN = "tristanoscatering.ca";

function cookieDomainAttr(hostname: string): string {
  if (hostname === SHARED_PARENT_DOMAIN || hostname.endsWith(`.${SHARED_PARENT_DOMAIN}`)) {
    return `; Domain=.${SHARED_PARENT_DOMAIN}`;
  }
  return ""; // localhost, *.vercel.app previews, etc. — host-only cookie
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentChoice | null {
  try {
    const v = readCookie(COOKIE_NAME);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(choice: ConsentChoice): void {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    const domain = cookieDomainAttr(window.location.hostname);
    document.cookie = `${COOKIE_NAME}=${choice}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/${domain}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked (private mode, etc.) — the choice just won't persist.
  }
}

/** Opt-out: analytics are allowed unless the visitor has explicitly declined. */
export function analyticsAllowed(): boolean {
  return getConsent() !== "declined";
}

/** Clear the stored consent choice (test teardown). */
export function clearConsent(): void {
  try {
    const domain = cookieDomainAttr(window.location.hostname);
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/${domain}`;
  } catch {
    // best-effort
  }
}
